import type {
  Filter,
  FilterOption,
  FilterType,
  SelectedFilters,
} from "shared/types";
import { FILTER_TYPES } from "shared/constants";

type RawFilterResource = {
  name: string;
  label: string;
  type: string;
  options?: { value: unknown; label: string }[];
  defaultValue: unknown;
};

export const mapFilterOption = <TValue = string | number | boolean>(option: {
  value: unknown;
  label: string;
}): FilterOption<TValue> => ({
  value: option.value as TValue,
  label: option.label,
});

export const mapFilter = <
  TName extends string = string,
  TType extends FilterType = FilterType,
  TValue = string | number | boolean,
  TDefault = TValue | TValue[] | null,
>(
  resource: RawFilterResource,
): Filter<TName, TType, TValue, TDefault> => ({
  name: resource.name as TName,
  label: resource.label,
  type: resource.type as TType,
  ...(resource.options && {
    options: resource.options.map((option) => mapFilterOption<TValue>(option)),
  }),
  defaultValue: resource.defaultValue as TDefault,
});

const hasNoOptions = (resource: RawFilterResource): boolean =>
  Array.isArray(resource.options) && resource.options.length === 0;

export const mapFilters = <
  TName extends string = string,
  TType extends FilterType = FilterType,
  TValue = string | number | boolean,
  TDefault = TValue | TValue[] | null,
>(
  resources: RawFilterResource[],
): Filter<TName, TType, TValue, TDefault>[] =>
  resources
    .filter((resource) => !hasNoOptions(resource))
    .map((resource) => mapFilter<TName, TType, TValue, TDefault>(resource));

/**
 * Maps the raw filter payload of the cluster- and learning objective library.
 * The payload comes from the legacy (untyped) JS store, so it is narrowed here
 * instead of at the call site. The backend serialises the search filter as type
 * "text"; FilterPanel only renders the inline search input for type "search",
 * so it is remapped accordingly.
 */
export const mapLibraryFilters = (resources: unknown): Filter[] => {
  const rawFilters = Array.isArray(resources)
    ? (resources as RawFilterResource[])
    : [];

  return mapFilters(rawFilters).map((filter) =>
    filter.name === "search"
      ? { ...filter, type: FILTER_TYPES.SEARCH }
      : filter,
  );
};

/**
 * Converts SelectedFilters to Record<string, string> for API calls.
 * - Filters out null/undefined values
 * - Converts arrays to comma-separated strings
 * - Converts other values to strings
 */
// Pagination metadata fields that should not be sent to the API
const PAGINATION_META_KEYS = new Set(["last_page", "from", "to", "total"]);

export const selectedFiltersToQueryParams = (
  filters: SelectedFilters,
): Record<string, string> => {
  const params: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    // Skip pagination metadata fields
    if (PAGINATION_META_KEYS.has(key)) {
      return;
    }

    if (value === null || value === undefined) {
      return;
    }

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return;
    }

    // Skip empty strings
    if (typeof value === "string" && value.length === 0) {
      return;
    }

    params[key] = Array.isArray(value) ? value.join(",") : String(value);
  });

  return params;
};
